import IntranetLayout from "@/components/IntranetLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Handshake, Plus, Calendar, Building2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const EmployeeContracts = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ client_name: "", contract_number: "", start_date: "", end_date: "", status: "active", summary: "", value: "" });

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: ["contracts"],
    queryFn: async () => {
      const { data, error } = await supabase.from("contracts").select("*").order("end_date", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const addMutation = useMutation({
    mutationFn: async (values: typeof form) => {
      const { error } = await supabase.from("contracts").insert({
        ...values,
        start_date: values.start_date || null,
        end_date: values.end_date || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast.success("Contract added");
      setForm({ client_name: "", contract_number: "", start_date: "", end_date: "", status: "active", summary: "", value: "" });
      setDialogOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const statusColors: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    active: "default",
    pending: "secondary",
    expired: "destructive",
    completed: "outline",
  };

  return (
    <IntranetLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-foreground">Contracts</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Client contracts, key dates, and summaries</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => toast.success("Created: Add Contract", { description: "New entry added." })} size="sm" className="bg-teal hover:bg-teal-light text-white gap-2"><Plus className="w-4 h-4" /> Add Contract</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Contract</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); addMutation.mutate(form); }} className="space-y-4">
              <Input placeholder="Client Name (e.g., ExxonMobil)" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Contract Number" value={form.contract_number} onChange={(e) => setForm({ ...form, contract_number: e.target.value })} />
                <Input placeholder="Value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-muted-foreground font-body block mb-1">Start Date</label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div><label className="text-xs text-muted-foreground font-body block mb-1">End Date</label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              </div>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
              <Textarea placeholder="Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3} />
              <Button type="submit" className="w-full bg-teal hover:bg-teal-light text-white" disabled={addMutation.isPending}>Add Contract</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
      ) : contracts.length === 0 ? (
        <Card><CardContent className="p-12 text-center"><Handshake className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground font-body">No contracts added yet</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {contracts.map((c: any) => (
            <Card key={c.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Building2 className="w-5 h-5 text-teal" />
                    </div>
                    <div>
                      <h3 className="font-display text-base text-foreground">{c.client_name}</h3>
                      <p className="text-xs text-muted-foreground font-body">{c.contract_number || "No contract #"}{c.value ? ` · ${c.value}` : ""}</p>
                      {c.summary && <p className="text-sm text-muted-foreground/80 font-body mt-2 line-clamp-2">{c.summary}</p>}
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground font-body">
                        {c.start_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(c.start_date).toLocaleDateString()}</span>}
                        {c.end_date && <span>→ {new Date(c.end_date).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </div>
                  <Badge variant={statusColors[c.status] || "outline"}>{c.status}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </IntranetLayout>
  );
};

export default EmployeeContracts;
